import {
  ASSET_SYMBOL,
  UNIT_OF_ASSETS_QUANTITY,
  VANILLA_SETTLING_ASSET,
} from 'constants/constants';
import { MARGIN_MODE, SIDE } from 'constants/enums';
import {
  assetPrecision,
  convertExponentialToDecimal,
  convertOrderBook,
  decimalMultiplication,
  isOptions,
  isTurboOptions,
  negativeBalanceCheck,
} from 'helpers/assetUtils';
import { divide, isEmpty, isNil } from 'helpers/ramda';
import {
  calcPrecision,
  cropAfterDecimals,
  isFutures,
  isNan,
  isSpreads,
} from 'helpers/utils';
// eslint-disable-next-line import/no-cycle
import { isAssetEqualToLotOrCont } from 'helpers/validations';
import { IBuySell } from 'reducers/l2Orderbook';
import StoreProxy from 'storeProxy';
import { selectedProductMarkPriceSelector } from 'stores/selectedProduct/selectedProduct.selectors';
import { OpenOrder } from 'types/IOpenOrders';
import { OpenPosition } from 'types/IOpenPosition';
import { ContractType, Product } from 'types/IProducts';
import { IMultiCollateral } from 'types/IVariableStore';
import { IBalance } from 'types/IWallet';

import {
  calculateOffsetMargins,
  isSpotContract,
  limitPriceBasedOnContractType,
  notional,
  offsetOpenPositionsFromOrders,
  sanitizeBuySellPairs,
} from './commonFormulae';
import { getCrossAvailablebalance } from './crossFormulae';
import {
  computeFuturesMaxQuantity,
  contractToAssetForFutures,
  marginCalculationForFutures,
  quantityToContractsForFutures,
} from './futuresFormulae';
import {
  calculateEstimateMarginForPlaceOrderOptions,
  contractsToQuantityForOptions,
  getOptionsOffsetMax,
  maxQuantityForTurboOptions,
  quantityToContractsForOptions,
} from './optionsFormulae';
import {
  convertOneContractToOtherInSpot,
  priceToCalculateQuantitySelector,
  spotBalanceSymbol,
  spotHintText,
  spotProductAvailableBalanceWithoutTradingCredit,
} from './spotFormulae';
// eslint-disable-next-line import/no-cycle
import { marginCalculationForSpreads, maxQuantityForSpreads } from './spreads';

const decimalHandle = (inputedQuantity: number) => {
  switch (inputedQuantity?.toString?.()) {
    case '.':
    case '0.':
    case '.0':
      return 0;
    default:
      return inputedQuantity;
  }
};

/**
 * Calculates the total position size for a selected product across all open positions.
 *
 * @param allOpenPositions - An array of all open positions
 * @param product - The selected product to calculate the position size for
 * @returns The total position size for the selected product
 *
 * This function is used to sum up the sizes of all open positions for a specific product.
 * It's used in the place order v2 to calculate the total position size for a product.
 * when we click on the percentages this is used to calculate the input value based on the percentage clicked
 * traceback: positionSizeForSelectedProduct -> getTotalPositonSize (src/selectors/placeOrderSelector.ts:149) -> optionsMaxQuantitySize (src/selectors/placeOrderSelector.ts:167)
 */
const positionSizeForSelectedProduct = (
  allOpenPositions: OpenPosition[],
  product: Product | null
) => {
  const positionSize = allOpenPositions.reduce((acc: number, position: OpenPosition) => {
    if (position?.product?.id === product?.id) {
      return acc + Number(position?.size);
    }
    return acc;
  }, 0);

  return positionSize;
};

/**
 * used to show the symbol in available balance
 * @param product - The product to get the balance symbol for
 * @param side - The side of the product
 * @param marginMode - The margin mode of the product
 * @returns The balance symbol for the product
 * Traceback: src/components/placeorder_v2/AvailableMargin/AvailableMargin.tsx:40
 */
const getBalanceSymbol = ({
  product,
  side,
  marginMode,
}: {
  product: Product;
  side: SIDE;
  marginMode: MARGIN_MODE;
}): string => {
  try {
    if (!product) {
      throw new Error('Product is required');
    }
    if (!side) {
      throw new Error('Side is required');
    }
    if (!marginMode) {
      throw new Error('Margin mode is required');
    }
    const contractType = product?.contract_type;
    let balanceSymbol = product?.settling_asset?.symbol;

    if (isSpotContract(contractType)) {
      balanceSymbol = spotBalanceSymbol({ side, product });
    }

    if (marginMode === MARGIN_MODE.CROSS && !isSpotContract(contractType)) {
      balanceSymbol = ASSET_SYMBOL;
    }
    // when there is no selected product, falling back to default settling asset of exchange
    // example: when user directly jumps to basket order screen from options in markets tab mobile view
    //          because no product is selected
    return balanceSymbol ?? String(VANILLA_SETTLING_ASSET);
  } catch (error) {
    console.error('Error in getBalanceSymbol:', error);
    return '';
  }
};

/**
 * used to calculate the available balance
 * @param marginMode - The margin mode of the product
 * @param tradingCredits - The trading credits of the product
 * @param multiCollateralData - The multi collateral data of the product
 * @param productBalance - The product balance of the product
 * @param contractType - The contract type of the product
 * @param isBuyAction - The side of the product
 * @param balanceSymbol - The balance symbol of the product
 * @returns The available balance of the product
 * Traceback: src/components/placeorder_v2/AvailableMargin/AvailableMargin.tsx:40
 */
const getAvailableBalance = ({
  marginMode,
  tradingCredits,
  multiCollateralData,
  productBalance,
  contractType,
  isBuyAction,
  balanceSymbol,
  side,
}: {
  marginMode: MARGIN_MODE;
  tradingCredits: Record<string, { amount: number }>;
  multiCollateralData: IMultiCollateral;
  productBalance: IBalance;
  contractType: ContractType;
  isBuyAction: boolean;
  balanceSymbol: string;
  side: SIDE;
}): number | string | null => {
  let balance: number | null = Number(productBalance?.available_balance);

  if (marginMode === MARGIN_MODE.CROSS) {
    balance = getCrossAvailablebalance({
      side,
      contractType,
      multiCollateralData,
      productBalance,
      balanceSymbol,
    });
  }

  /**
   * User should not be allowed to purchase through spot market using trading credits.
   * So deducting trading credits from balance.
   */
  if (contractType === ContractType.Spot && isBuyAction) {
    balance = spotProductAvailableBalanceWithoutTradingCredit({
      productBalance,
      tradingCredits,
      balance: Number(balance),
    });
  }

  // * values less than zero & in decimals like '0.000000653' get converted to 6.53e-7 with above functions, should be converted back to decimal
  return convertExponentialToDecimal(balance);
};

/**
 * used to get the selected product balance
 * @param product - The product to get the balance for
 * @param allBalances - The all balances of the product
 * @param marginMode - The margin mode of the product
 * @param tradingCredits - The trading credits of the product
 * @param multiCollateralData - The multi collateral data of the product
 * @param side - The side of the product
 * @returns The selected product balance
 * Traceback: src/components/placeorder_v2/AvailableMargin/AvailableMargin.tsx:40, in calculating max size for percentage click. src/formulae/index.ts:367
 */
const getSelectedProductBalance = (
  product: Product | null,
  allBalances: { [symbol: string]: IBalance },
  marginMode: MARGIN_MODE,
  tradingCredits: Record<string, { amount: number }>,
  multiCollateralData: IMultiCollateral,
  side: SIDE
) => {
  const contractType = product?.contract_type ?? ContractType.Futures;
  const isBuyAction = side === SIDE.BUY;
  const balanceSymbol = getBalanceSymbol({
    product: product ?? {},
    side,
    marginMode,
  });

  const productBalance = allBalances?.[balanceSymbol ?? ''];
  const precision = productBalance && assetPrecision(productBalance?.asset ?? {});
  const balance = getAvailableBalance({
    marginMode,
    tradingCredits,
    multiCollateralData,
    productBalance,
    contractType,
    isBuyAction,
    balanceSymbol,
    side,
  });
  const roundedBalance = negativeBalanceCheck(balance, precision);

  return {
    balance,
    roundedBalance,
    balanceSymbol,
  };
};

/**
 * used to get the max size for non spot products. Used to determine input value based on percentage clicked
 * @param product - The product to get the max size for
 * @param orderbooks - The orderbooks of the product
 * @param side - The side of the product
 * @param leverage - The leverage of the product
 * @param balance - The balance of the product
 * @param allSpotPrice - The all spot prices of the product
 * @returns The max size for the product
 * Traceback: src/selectors/placeOrderSelector.ts:481
 */
const getNonSpotMaxSize = (
  product: Product | null,
  orderbooks: { buy: IBuySell[]; sell: IBuySell[] },
  side: SIDE,
  leverage: number,
  { balance = 0 }: { balance: number },
  allSpotPrice: Record<string, number>,
  allMarkPrice: Record<string, number>,
  totalPositionSize: number,
  limitPrice: number | null
): number => {
  try {
    const contractType = product?.contract_type;
    if (contractType === 'spot') {
      return 0;
    }

    if (!product) {
      throw new Error('Product is required');
    }

    const id = product?.id;
    const spotIndex = product?.spot_index;
    const commissionRate = Number(product?.taker_commission_rate);

    if (!spotIndex?.symbol) {
      throw new Error('Spot index is required');
    }
    if (!id) {
      throw new Error('product id not found');
    }
    if (!commissionRate || isNan(commissionRate)) {
      throw new Error('Commission rate is required');
    }
    const notionalType = product?.notional_type;
    const spotPrice = allSpotPrice?.[spotIndex?.symbol];
    const markPrice = allMarkPrice?.[id];
    const availableBalance = Math.max(balance, 0.0);
    const orderbook = convertOrderBook(orderbooks, side);
    const entryPrice = limitPrice ?? Number(markPrice);

    if (isOptions(contractType)) {
      /**
       * price = side === 'buy' ? entry price : margin required
       * maxNumberofContracts = availableBalance/ (price * contract_value + 3* Fee)
       * MaxQuantity = maxNumberofContracts + totalPositionSize
       *
       * offsettedMax = min((product.position_size_limit - totalPositionSize), MaxQuantity)
       */
      return getOptionsOffsetMax({
        availableBalance,
        orderbook,
        product,
        limitPrice,
        side,
        spotPrice,
        commissionRate,
        totalPositionSize,
        leverage,
      });
    }

    if (isFutures(contractType)) {
      /**
       * leverageFactor = 1 / leverage;
       * totalCommission = 2 * commissionRate;
       * totalCost = leverageFactor + totalCommission;
       * Formula for vanilla: maxQuantity = availableBalance / (entryPrice * totalCost)
       * Formula for inverse: maxQuantity = (entryPrice * availableBalance) / totalCost
       */
      return computeFuturesMaxQuantity({
        availableBalance,
        entryPrice,
        leverage,
        commissionRate,
        notionalType,
        side,
        totalPositionSize,
        product,
      });
    }
    if (isSpreads(contractType)) {
      /**
       * leverageFactor = 1 / leverage;
       * totalCommission = 2 * commissionRate;
       * totalCost = leverageFactor + totalCommission;
       *
       * maxQuantity = availableBalance / (totalCost * spotPrice);
       */
      return maxQuantityForSpreads({
        availableBalance,
        leverage,
        commissionRate,
        product,
        side,
        totalPositionSize,
      });
    }

    if (isTurboOptions(contractType)) {
      /**
       * totalCommission = 2 * commissionRate;
       * totalCost = spotPrice * totalCommission;
       * totalCostForBuy = totalCost + entryPrice;
       * totalCostForSell = totalCost + spotPrice / leverage;
       *
       * maxQuantity = availableBalance / (totalCostForBuy or sell);
       */
      return maxQuantityForTurboOptions({
        availableBalance,
        leverage,
        commissionRate,
        spotPrice,
        entryPrice,
        side,
      });
    }

    throw new Error('Invalid contract type');
  } catch (error) {
    console.error('Error in getNonSpotMaxSize:', error);
    return 0;
  }
};

const getMarkPrice = (tickSize: number) => {
  const precision = calcPrecision(tickSize);
  const markPrice = selectedProductMarkPriceSelector(precision);
  return markPrice;
};

/**
 * used to convert the quantity value to the number of contracts
 * @param inputedQuantity - The quantity value to convert
 * @param selectedProduct - The product to convert the quantity value to
 * @param side - The side of the product
 * @returns The number of contracts
 * Traceback: src/components/placeorder_v2/QuantityInput/calculateAllConvertedValues.thunk.ts:42
 */
const convertQuantityValueToNumberOfContracts = (
  inputedQuantity: number,
  selectedProduct: Product,
  side: SIDE
): number => {
  try {
    if (!selectedProduct) {
      throw new Error('Selected product is required');
    }
    if (!side) {
      throw new Error('Side is required');
    }
    const markPrice = isNan(getMarkPrice(Number(selectedProduct?.tick_size)))
      ? 0
      : Number(getMarkPrice(Number(selectedProduct?.tick_size)));
    let price = markPrice;

    if (
      !inputedQuantity ||
      !price ||
      inputedQuantity === 0 ||
      !String(inputedQuantity).length
    )
      return 0;
    const settlingAsset = selectedProduct?.settling_asset;
    const settlingAssetSymbol = settlingAsset?.symbol;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const underlyingAssetSymbol = underlyingAsset?.symbol;
    const contractValue = Number(selectedProduct?.contract_value ?? '1');

    const state = StoreProxy.getState();
    const selectedCurrency = state.placeOrder?.dropdownValue;

    const contractType = selectedProduct?.contract_type;

    if (isSpotContract(contractType)) {
      /**
       * quotePrice = aggregate quote size / aggregate size
       * nonQuotePrice = Σ(levelPrice * (matchSize / filledSize))
       * impactPrice = isQuoteAsset ? quotePrice : nonQuotePrice
       * price = price from orderbook || impactPrice
       */
      price = priceToCalculateQuantitySelector({
        selectedProduct,
        dropDownValue: selectedCurrency,
        inputedQuantity,
        side,
      });
    }
    if (isFutures(contractType)) {
      /**
       * for Vanilla Futures:
       *  for underlyingAssetSymbol =  qty / contractValue
       *  for settlingAssetSymbol = qty / (contractValue * markPrice)
       *
       * for Inverse Futures:
       *  for underlyingAssetSymbol =  (qty * markPrice) / contractValue
       *  for settlingAssetSymbol = qty / contractValue
       */
      return quantityToContractsForFutures({
        inputedQuantity,
        selectedProduct,
        selectedCurrency,
      });
    }
    if (isOptions(contractType)) {
      /**
       * for Options:
       *  for underlyingAssetSymbol = qty / contractValue
       *  for settlingAssetSymbol = qty / (contractValue * spotPrice)
       */
      return quantityToContractsForOptions({
        inputedQuantity,
        selectedProduct,
        selectedCurrency,
      });
    }
    if (selectedCurrency === underlyingAssetSymbol) {
      return parseInt(String(divide(inputedQuantity, contractValue)), 10);
    }
    if (selectedCurrency === settlingAssetSymbol) {
      const cXp: number = Number(decimalMultiplication(contractValue, price));
      return parseInt(String(divide(inputedQuantity, cXp)), 10);
    }
    return parseInt(String(Number(decimalHandle(inputedQuantity))), 10);
  } catch (error) {
    console.error('Error in convertQuantityValueToNumberOfContracts:', error);
    return 0;
  }
};

/**
 * Calculate the default contract value (the value for first option in dropdown) based on the order size, contract value, product type, and the quoting and underlying assets.
 * i.e. if the first quantity input dropdown is BTC and second is USDT. this fn converts `number of contracts` to BTC
 * @param orderSize The size of the order in number of contracts.
 * @param contractValue The value of a single contract in the settling asset.
 * @param productType The type of the product, either 'inverse' or 'linear'.
 * @param quotingAsset The asset that the order is quoted in.
 * @param underlyingAsset The asset that the contract is settled in.
 * @returns The default contract value in the settling asset.
 */

const calculateDefaultContractValue = (
  orderSize: number,
  contractValue: number,
  productType: string,
  quotingAsset: { minimum_precision: number },
  underlyingAsset: { minimum_precision: number }
) => {
  // Early return for invalid orderSize to avoid unnecessary calculations
  if (!String(orderSize).length) return 0;

  // Use ternary operator for concise precision determination
  const precision =
    productType === 'inverse'
      ? quotingAsset.minimum_precision
      : underlyingAsset?.minimum_precision || 2;

  // Consolidate Number conversions and toFixed operation
  return Number((orderSize * contractValue).toFixed(precision));
};

/**
 * Convert the number of contracts to the selected currency from the quantity input dropdown.
 * @param qtyInContracts The number of contracts to convert.
 * @param selectedProduct The product to convert from.
 * @param asset Selected dropdown value from the quantity input dropdown.
 * @param price The price of the asset.
 * @param side The side of the order.
 * @param forErrMsg for error message, no need of adding extra zeroes in options contracts
 * @returns The number of contracts in the selected currency.
 * Traceback: src/components/placeorder_v2/QuantityInput/handleErrors.thunk.ts:45, src/components/placeorder_v2/QuantityInput/calculateAllConvertedValues.thunk.ts:54
 */

const convertNumberOfContractsToSelectedCurrency = (
  qtyInContracts: number,
  selectedProduct: Product | null,
  asset: string,
  side: SIDE,
  forErrMsg = false
) => {
  try {
    if (isNan(qtyInContracts) || qtyInContracts === null || qtyInContracts === 0) {
      return null;
    }
    if (isAssetEqualToLotOrCont(asset)) return qtyInContracts;

    const settlingAsset = selectedProduct?.settling_asset;
    const settlingAssetSymbol = settlingAsset?.symbol;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const underlyingAssetSymbol = underlyingAsset?.symbol;
    const contractValue = Number(selectedProduct?.contract_value ?? '1');

    const contractType = selectedProduct?.contract_type;

    // Formulas
    if (isFutures(contractType)) {
      /**
       * for Vanilla Futures:
       *  for underlyingAssetSymbol =  orderSize * contractValue
       *  for settlingAssetSymbol = orderSize * contractValue * markPrice
       *
       * for Inverse Futures:
       *  for underlyingAssetSymbol =  orderSize * contractValue
       *  for settlingAssetSymbol = (orderSize * contractValue) / markPrice
       */
      return contractToAssetForFutures({
        orderSize: qtyInContracts,
        selectedProduct,
        selectedCurrency: asset,
      });
    }

    if (isOptions(contractType)) {
      /**
       * for settlingAssetSymbol = orderSize * contractValue * spotPrice
       * for underlyingAssetSymbol = orderSize * contractValue
       */
      return contractsToQuantityForOptions({
        orderSize: qtyInContracts,
        selectedProduct,
        selectedCurrency: asset,
        forErrMsg,
      });
    }

    if (isSpotContract(contractType)) {
      /**
       * for quotingAsset = (impactPrice * Σ(orderbook.size)) + (worstPrice * (orderSize - Σ(orderbook.size)))
       * for others = (impactPrice * Σ(orderbook.size)) + (worstPrice * Σ(orderbook.size))
       */
      return convertOneContractToOtherInSpot({
        selectedProduct,
        selectedCurrency: asset,
        orderSize: qtyInContracts,
        side,
      });
    }

    const formula = qtyInContracts * contractValue;
    if (asset === underlyingAssetSymbol) {
      return cropAfterDecimals(formula, underlyingAsset?.minimum_precision);
    }

    if (asset === settlingAssetSymbol) {
      const markPrice = isNan(getMarkPrice(Number(selectedProduct?.tick_size)))
        ? 0
        : Number(getMarkPrice(Number(selectedProduct?.tick_size)));
      return cropAfterDecimals(formula * markPrice, settlingAsset?.minimum_precision);
    }

    return parseInt(String(Number(decimalHandle(qtyInContracts))), 10);
  } catch (error) {
    console.error('Error in convertNumberOfContractsToSelectedCurrency:', error);
    return null;
  }
};

/**
 * Calculate the hint text to show in the quantity input.
 * @param quantityCurrency The currency of the quantity input dropdown.
 * @param selectedProduct The product to calculate the hint text for.
 * @param qtyInContracts The value of the quantity input dropdown in terms of contracts or the selected currency in spot.
 * @returns The hint text to show in the quantity input dropdown.
 */
const hintTextToShowInQuantityInput = (
  allConvertedCurrencyValues: Record<string, number | string>,
  quantityCurrency: string,
  selectedProduct: Product | null,
  actualQtyValue: number | string | null,
  dropDownValues: string[]
): string | null => {
  try {
    if (actualQtyValue === null) {
      // placeholder and hint text were overlapping
      return null;
    }
    if (isSpotContract(selectedProduct?.contract_type)) {
      return spotHintText(allConvertedCurrencyValues, quantityCurrency, dropDownValues);
    }

    const qtyInContracts = allConvertedCurrencyValues?.[
      UNIT_OF_ASSETS_QUANTITY
    ] as number;

    if (!qtyInContracts || qtyInContracts < 0) {
      return '';
    }

    if (quantityCurrency !== UNIT_OF_ASSETS_QUANTITY) {
      return `~${qtyInContracts} ${UNIT_OF_ASSETS_QUANTITY.toLowerCase()}`;
    }
    if (allConvertedCurrencyValues?.[selectedProduct?.contract_unit_currency as string]) {
      return `~${
        allConvertedCurrencyValues?.[selectedProduct?.contract_unit_currency as string]
      } ${selectedProduct?.contract_unit_currency}`;
    }
    return '';
  } catch (error) {
    console.error('Error in hintTextToShowInQuantityInput:', error);
    return '';
  }
};

/**
 *
 * @param size size of the order
 * @param limitPrice limit price of the order
 * @param selectedProduct product for which the margin is to be calculated
 * @param side side of the order
 * @param leverage leverage of the order
 * @returns margin for the order
 */
const marginFormulaeForContracts = (
  size: number,
  limitPrice: string | null,
  selectedProduct: Product | null,
  side: SIDE,
  leverage: number
) => {
  try {
    if (!selectedProduct) {
      throw new Error('Selected product is required');
    }
    const price = limitPrice ?? getMarkPrice(Number(selectedProduct?.tick_size));

    const contractType = selectedProduct?.contract_type;

    if (isOptions(contractType) || isTurboOptions(contractType)) {
      return calculateEstimateMarginForPlaceOrderOptions(
        size,
        price, // should be premium
        selectedProduct,
        side,
        leverage
      );
    }

    if (contractType === ContractType.Spreads) {
      // notion / leverage
      return marginCalculationForSpreads(size, leverage, selectedProduct as Product);
    }

    if (isFutures(contractType)) {
      // notion / leverage
      return marginCalculationForFutures(
        size,
        price,
        leverage,
        selectedProduct as Product
      );
    }

    return notional(size, price, selectedProduct as Product);
  } catch (error) {
    console.error('Error in marginFormulaeForContracts:', error);
    return null;
  }
};

// todo - below function has missing logic currently. needs to be re implemented once all placeorder forms are on v2. it will be called in calculateExtraOrderMargin src/helpers/formulas.js directly replacing its function body.

/**
 * Calculate the estimate margin for the place order.
 * @param size size of the order
 * @param limitPrice limit price of the order
 * @param selectedProduct product for which the margin is to be calculated
 * @param side side of the order
 * @param leverage leverage of the order
 * @param openBuyOrder open buy orders
 * @param openSellOrder open sell orders
 * @param openSellOrder open sell orders
 * @param selectedProductOpenPosition open position of the selected product
 * @param selectedProductOrderMargin margin of the selected product
 * @returns margin for the order
 */
const calculateEstimateMarginForPlaceOrder = (
  size: number,
  limitPrice: number,
  selectedProduct: Product | null,
  side: SIDE,
  leverage: number,
  openBuyOrder: OpenOrder[],
  openSellOrder: OpenOrder[],
  selectedProductOpenPosition: OpenPosition,
  selectedProductOrderMargin: number
): number | undefined => {
  try {
    if (isNil(size) || isNil(leverage) || leverage === 0) {
      // shows - when undefined is returned
      return undefined;
    }
    const tickSize = selectedProduct?.tick_size;
    const tickPrecision = calcPrecision(tickSize);

    const markPriceState = selectedProductMarkPriceSelector(tickPrecision);

    const price = limitPrice ?? markPriceState;

    if (isEmpty(leverage)) {
      return 0;
    }
    if (isNil(price)) {
      return 0;
    }

    // sort and add current order to open orders
    const { sortedOpenBuyOrder, sortedOpenSellOrder } = sanitizeBuySellPairs({
      openBuyOrder,
      openSellOrder,
      size,
      price,
      side,
    });
    // offset open positions from orders
    const { adjustedBuyOrders, adjustedSellOrders } = offsetOpenPositionsFromOrders({
      position: selectedProductOpenPosition,
      buyOrders: sortedOpenBuyOrder,
      sellOrders: sortedOpenSellOrder,
    });

    // calculate offsetted margin pairs
    const offsettedMarginPairs = calculateOffsetMargins({
      buyPairs: adjustedBuyOrders,
      sellPairs: adjustedSellOrders,
    });
    const totalMargin = offsettedMarginPairs?.reduce(
      (acc, { size: offsettedSize, buyLimitPrice, sellLimitPrice }) => {
        const { limitPrice: limitPriceToBeUsed, side: sideToBeUsed } =
          limitPriceBasedOnContractType({
            selectedProduct,
            buyLimitPrice,
            sellLimitPrice,
          });
        if (!limitPriceToBeUsed) {
          return acc;
        }
        return (
          acc +
          marginFormulaeForContracts(
            offsettedSize ?? 0,
            limitPriceToBeUsed ?? 0,
            selectedProduct ?? {},
            sideToBeUsed ?? SIDE.BUY,
            leverage ?? 0
          )
        );
      },
      0
    );
    return Math.max(totalMargin - selectedProductOrderMargin, 0);
  } catch (error) {
    console.error('Error in calculateEstimateMarginForPlaceOrder:', error);
    return undefined;
  }
};

export {
  calculateDefaultContractValue,
  calculateEstimateMarginForPlaceOrder,
  convertNumberOfContractsToSelectedCurrency,
  convertQuantityValueToNumberOfContracts,
  decimalHandle,
  getAvailableBalance,
  getBalanceSymbol,
  getNonSpotMaxSize,
  getSelectedProductBalance,
  hintTextToShowInQuantityInput,
  positionSizeForSelectedProduct,
};
